import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import CodeBlock from '../CodeBlock';

const JavaScriptSDK = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        const response = await fetch('/docs/sdks/javascript-sdk.md');
        const text = await response.text();
        setContent(text);
      } catch (error) {
        console.error('Error loading JavaScript SDK documentation:', error);
        setContent('# Error loading documentation\n\nPlease try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadMarkdown();
  }, []);

  const renderMarkdown = (markdown) => {
    if (!markdown) return null;

    const lines = markdown.split('\n');
    const elements = [];
    let currentSection = [];
    let inCodeBlock = false;
    let codeBlockLanguage = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Handle code blocks
      if (line.startsWith('```')) {
        if (!inCodeBlock) {
          inCodeBlock = true;
          codeBlockLanguage = line.slice(3).trim();
        } else {
          inCodeBlock = false;
          elements.push(
            <CodeBlock
              key={`code-${i}`}
              code={currentSection.join('\n')}
              language={codeBlockLanguage || 'text'}
            />
          );
          currentSection = [];
        }
        continue;
      }

      if (inCodeBlock) {
        currentSection.push(line);
        continue;
      }

      // Handle headers
      if (line.startsWith('# ')) {
        if (currentSection.length > 0) {
          elements.push(<div key={`text-${i}`} className="whitespace-pre-line mb-4">{currentSection.join('\n')}</div>);
          currentSection = [];
        }
        elements.push(
          <h1 key={`h1-${i}`} className="text-3xl font-bold text-foreground mb-6 mt-8">
            {line.slice(2)}
          </h1>
        );
      } else if (line.startsWith('## ')) {
        if (currentSection.length > 0) {
          elements.push(<div key={`text-${i}`} className="whitespace-pre-line mb-4">{currentSection.join('\n')}</div>);
          currentSection = [];
        }
        elements.push(
          <h2 key={`h2-${i}`} className="text-2xl font-semibold text-foreground mb-4 mt-6">
            {line.slice(3)}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        if (currentSection.length > 0) {
          elements.push(<div key={`text-${i}`} className="whitespace-pre-line mb-4">{currentSection.join('\n')}</div>);
          currentSection = [];
        }
        elements.push(
          <h3 key={`h3-${i}`} className="text-xl font-semibold text-foreground mb-3 mt-4">
            {line.slice(4)}
          </h3>
        );
      } else if (line.startsWith('- ')) {
        // Handle list items
        const lastSection = currentSection.length > 0 ? currentSection[currentSection.length - 1] : null;
        if (lastSection && typeof lastSection === 'string' && !lastSection.startsWith('- ')) {
          elements.push(<div key={`text-${i}`} className="whitespace-pre-line mb-4">{currentSection.join('\n')}</div>);
          currentSection = [];
        }
        currentSection.push(
          <div key={`li-${i}`} className="flex items-start space-x-2 mb-2">
            <Icon name="ChevronRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <span className="text-foreground">{line.slice(2)}</span>
          </div>
        );
      } else if (line.trim() === '') {
        // Handle empty lines
        if (currentSection.length > 0) {
          elements.push(<div key={`text-${i}`} className="whitespace-pre-line mb-4">{currentSection.join('\n')}</div>);
          currentSection = [];
        }
      } else {
        currentSection.push(line);
      }
    }

    // Add any remaining content
    if (currentSection.length > 0) {
      elements.push(<div key="text-final" className="whitespace-pre-line mb-4">{currentSection.join('\n')}</div>);
    }

    return elements;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h1 className="text-3xl font-bold text-foreground mb-4">JavaScript SDK</h1>
        <p className="text-lg text-muted-foreground">
          Build powerful messaging features into your JavaScript applications with our comprehensive SDK.
        </p>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Code" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Installation</h2>
        </div>
        <CodeBlock
          code="npm install messaginghub-js"
          language="bash"
          title="Install JavaScript SDK"
          description="Add the SDK to your project using npm"
        />
      </div>

      <div className="prose prose-neutral max-w-none">
        {renderMarkdown(content)}
      </div>
    </div>
  );
};

export default JavaScriptSDK;
